export type ParsedPtsFormData = {
  project: string;
  task: string;
  estimationHours: string;
  workType: string;
};

function unwrapSelectLike(value: unknown): unknown {
  if (value && typeof value === "object" && !Array.isArray(value) && "value" in value) {
    return (value as { value: unknown }).value;
  }
  return value;
}

/** Extract a relation / select UUID from Formily or REST field values. */
export function extractFormilyFieldId(raw: unknown): string {
  const value = unwrapSelectLike(raw);
  if (value === undefined || value === null) {
    return "";
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    for (const key of ["id", "value", "uuid", "pk"]) {
      if (key in obj) {
        const id = String(obj[key] ?? "").trim();
        if (id && id !== "[object Object]") {
          return id;
        }
      }
    }
    return "";
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  const text = String(value).trim();
  if (!text || text === "null" || text === "undefined" || text === "[object Object]") {
    return "";
  }
  return text;
}

function firstFieldId(
  record: Record<string, unknown>,
  keys: readonly string[]
): string {
  for (const key of keys) {
    if (!(key in record)) {
      continue;
    }
    const id = extractFormilyFieldId(record[key]);
    if (id) {
      return id;
    }
  }
  return "";
}

function firstScalarString(
  record: Record<string, unknown>,
  keys: readonly string[]
): string {
  for (const key of keys) {
    if (!(key in record)) {
      continue;
    }
    const value = record[key];
    if (value === undefined || value === null || value === "") {
      continue;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
    const text = String(value).trim();
    if (text && text !== "null" && text !== "undefined") {
      return text;
    }
  }
  return "";
}

export function parseIsPtsFlag(record: Record<string, unknown>): boolean {
  const raw = record.is_pts ?? record.isPts ?? record.isPTS;
  if (raw === true || raw === 1) {
    return true;
  }
  if (typeof raw === "string") {
    const text = raw.trim().toLowerCase();
    return text === "true" || text === "1" || text === "yes";
  }
  return false;
}

/** Read PTS sub-task fields from a ticket Formily/REST record for edit pre-fill. */
export function parsePtsFromTicketRecord(
  record: Record<string, unknown>
): { includePts: boolean; ptsData: ParsedPtsFormData } {
  const ptsData: ParsedPtsFormData = {
    project: firstFieldId(record, ["project", "project_id", "pts_project"]),
    task: firstFieldId(record, [
      "parent_task",
      "parent_task_id",
      "task",
      "task_id",
      "pts_task",
      "pts_task_id",
    ]),
    estimationHours: firstScalarString(record, [
      "taskestimatedhours",
      "estimation_hours",
      "task_estimated_hours",
      "pts_estimation_hours",
    ]),
    workType: firstFieldId(record, [
      "work_type",
      "work_type_id",
      "worktype",
      "pts_work_type",
    ]),
  };

  const includePts =
    parseIsPtsFlag(record) ||
    Boolean(
      ptsData.project ||
        ptsData.task ||
        ptsData.workType ||
        ptsData.estimationHours
    );

  return { includePts, ptsData };
}
